import Interaction from 'ol/interaction/Interaction.js';
import type Map from 'ol/Map.js';
import type MapBrowserEvent from 'ol/MapBrowserEvent.js';

const PINCH_DELTA_PER_ZOOM = 20;
const CONTROL_WHEEL_DELTA_PER_ZOOM = 60;
const INTERACTION_END_DELAY_MS = 80;

export function pinchZoomDelta(deltaY: number, physicalControlKey = false): number {
  if (deltaY === 0) return 0;
  return -deltaY / (physicalControlKey ? CONTROL_WHEEL_DELTA_PER_ZOOM : PINCH_DELTA_PER_ZOOM);
}

/**
 * Handles browser trackpad pinch gestures using only OpenLayers' public
 * interaction and view APIs. The standard MouseWheelZoom interaction handles
 * non-ctrl wheel input; returning false here claims synthesized ctrl+wheel
 * pinch events before they reach it.
 */
export class TrackpadPinchZoom extends Interaction {
  private gesture: { status: 'idle' } | { status: 'zooming'; map: Map; timer: ReturnType<typeof setTimeout> | null } = { status: 'idle' };
  private physicalControlKey = false;
  private keyListeners: AbortController | null = null;

  override handleEvent(mapBrowserEvent: MapBrowserEvent<WheelEvent>): boolean {
    const event = mapBrowserEvent.originalEvent;
    if (!(event instanceof WheelEvent) || !event.ctrlKey) return true;

    event.preventDefault();
    const map = mapBrowserEvent.map;
    const view = map.getView();
    if (this.gesture.status === 'idle') {
      if (view.getAnimating()) view.cancelAnimations();
      view.beginInteraction();
      this.gesture = { status: 'zooming', map, timer: null };
    }
    if (this.gesture.status === 'zooming' && this.gesture.timer !== null) clearTimeout(this.gesture.timer);
    view.adjustZoom(
      pinchZoomDelta(event.deltaY, this.physicalControlKey),
      map.getCoordinateFromPixel(mapBrowserEvent.pixel),
    );
    this.gesture = { status: 'zooming', map, timer: setTimeout(() => this.finishInteraction(), INTERACTION_END_DELAY_MS) };
    return false;
  }

  override setMap(map: Map | null): void {
    const previousMap = this.getMap();
    if (previousMap !== map) this.finishInteraction();
    this.keyListeners?.abort();
    this.keyListeners = null;
    this.physicalControlKey = false;
    super.setMap(map);
    if (!map) return;
    const ownerDocument = map.getTargetElement()?.ownerDocument ?? document;
    this.keyListeners = new AbortController();
    const { signal } = this.keyListeners;
    ownerDocument.addEventListener('keydown', (event) => {
      if (event.key === 'Control') this.physicalControlKey = true;
    }, { signal });
    ownerDocument.addEventListener('keyup', (event) => {
      if (event.key === 'Control') this.physicalControlKey = false;
    }, { signal });
    ownerDocument.defaultView?.addEventListener('blur', () => {
      this.physicalControlKey = false;
    }, { signal });
  }

  private finishInteraction(): void {
    const gesture = this.gesture;
    if (gesture.status === 'idle') return;
    this.gesture = { status: 'idle' };
    if (gesture.timer !== null) clearTimeout(gesture.timer);
    gesture.map.getView().endInteraction();
  }
}
